import { useMemo, useRef, useState, useEffect } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from 'recharts'
import { cn } from '@/lib/utils'

interface DataPoint {
  date: string
  value: number
}

interface TrendChartProps {
  data: DataPoint[]
  color?: string
  height?: number
  showTrendline?: boolean
  showGrid?: boolean
  showTooltip?: boolean
  className?: string
}

/**
 * Calculate linear regression for trendline
 */
function calculateTrendline(data: DataPoint[]): { slope: number; intercept: number } {
  const n = data.length
  if (n < 2) return { slope: 0, intercept: 0 }

  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0
  
  data.forEach((point, i) => {
    sumX += i
    sumY += point.value
    sumXY += i * point.value
    sumX2 += i * i
  })

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)
  const intercept = (sumY - slope * sumX) / n

  return { slope, intercept }
}

/**
 * Custom tooltip component
 */
function CustomTooltip({ 
  active, 
  payload, 
}: { 
  active?: boolean
  payload?: Array<{ value: number; payload: { date: string } }>
}) {
  if (!active || !payload?.length) return null

  // Get date from the payload data, not from label (which may be undefined when XAxis is hidden)
  const dateStr = payload[0]?.payload?.date
  if (!dateStr) return null

  const date = new Date(dateStr)
  const formattedDate = date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric' 
  })

  return (
    <div className="bg-background/95 backdrop-blur-sm border border-white/[0.1] rounded-lg px-3 py-2 shadow-xl">
      <p className="text-[10px] text-muted-foreground/60 mb-1">{formattedDate}</p>
      <p className="text-sm font-semibold text-foreground tabular-nums">
        {payload[0].value.toLocaleString()}
      </p>
    </div>
  )
}

/**
 * Hook to measure container width
 */
function useContainerWidth(ref: React.RefObject<HTMLDivElement | null>) {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    if (!ref.current) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width)
      }
    })

    observer.observe(ref.current)
    // Initial measurement
    setWidth(ref.current.offsetWidth)

    return () => observer.disconnect()
  }, [ref])

  return width
}

export function TrendChart({
  data,
  color = '#63d706',
  height = 80,
  showTrendline = true,
  showGrid = false,
  showTooltip = true,
  className,
}: TrendChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const width = useContainerWidth(containerRef)

  // Add trendline data points
  const chartData = useMemo(() => {
    if (data.length === 0) return []

    const { slope, intercept } = calculateTrendline(data)
    
    return data.map((point, i) => ({
      ...point,
      trend: Math.max(0, intercept + slope * i),
    }))
  }, [data])

  if (data.length === 0) {
    return (
      <div 
        ref={containerRef}
        className={cn('flex items-center justify-center text-muted-foreground/30 text-xs', className)}
        style={{ height }}
      >
        No data
      </div>
    )
  }

  return (
    <div 
      ref={containerRef}
      className={cn('w-full', className)} 
      style={{ height }}
    >
      {width > 0 && (
        <AreaChart
          width={width}
          height={height}
          data={chartData}
          margin={{ top: 4, right: 4, left: 4, bottom: 4 }}
        >
          <defs>
            <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.4} />
              <stop offset="50%" stopColor={color} stopOpacity={0.15} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          
          {showGrid && (
            <>
              <XAxis 
                dataKey="date" 
                hide 
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                hide 
                axisLine={false}
                tickLine={false}
                domain={['dataMin', 'dataMax']}
              />
            </>
          )}
          
          {showTooltip && (
            <Tooltip 
              content={<CustomTooltip />}
              cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }}
            />
          )}
          
          {/* Main area */}
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#gradient-${color.replace('#', '')})`}
            animationDuration={800}
            animationEasing="ease-out"
          />
          
          {/* Trendline */}
          {showTrendline && chartData.length >= 3 && (
            <Area
              type="linear"
              dataKey="trend"
              stroke={color}
              strokeWidth={1}
              strokeDasharray="4 3"
              strokeOpacity={0.5}
              fill="none"
              animationDuration={1000}
              animationEasing="ease-out"
            />
          )}
        </AreaChart>
      )}
    </div>
  )
}

/**
 * Larger chart variant for detailed views
 */
interface DetailedChartProps {
  data: DataPoint[]
  color?: string
  height?: number
  title?: string
  className?: string
}

export function DetailedChart({
  data,
  color = '#63d706',
  height = 200,
  title,
  className,
}: DetailedChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const width = useContainerWidth(containerRef)

  const chartData = useMemo(() => {
    if (data.length === 0) return []

    const { slope, intercept } = calculateTrendline(data)
    
    return data.map((point, i) => ({
      ...point,
      trend: Math.max(0, intercept + slope * i),
      displayDate: new Date(point.date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      }),
    }))
  }, [data])

  if (data.length === 0) {
    return (
      <div 
        ref={containerRef}
        className={cn('flex items-center justify-center text-muted-foreground/30 text-xs', className)}
        style={{ height }}
      >
        No data available
      </div>
    )
  }

  return (
    <div className={cn('w-full', className)}>
      {title && (
        <div className="text-xs font-medium text-muted-foreground/70 mb-2">{title}</div>
      )}
      <div ref={containerRef} style={{ height }}>
        {width > 0 && (
          <AreaChart
            width={width}
            height={height}
            data={chartData}
            margin={{ top: 8, right: 8, left: 0, bottom: 24 }}
          >
            <defs>
              <linearGradient id={`gradient-detailed-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="50%" stopColor={color} stopOpacity={0.12} />
                <stop offset="100%" stopColor={color} stopOpacity={0.01} />
              </linearGradient>
            </defs>
            
            <XAxis 
              dataKey="displayDate" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
              width={32}
              tickFormatter={(value) => {
                if (value >= 1000) return `${(value / 1000).toFixed(0)}k`
                return value.toString()
              }}
            />
            
            <Tooltip 
              content={<CustomTooltip />}
              cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }}
            />
            
            {/* Horizontal grid lines */}
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.05)" />
            
            {/* Main area */}
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              fill={`url(#gradient-detailed-${color.replace('#', '')})`}
              animationDuration={800}
              animationEasing="ease-out"
            />
            
            {/* Trendline */}
            {chartData.length >= 3 && (
              <Area
                type="linear"
                dataKey="trend"
                stroke={color}
                strokeWidth={1.5}
                strokeDasharray="6 4"
                strokeOpacity={0.4}
                fill="none"
                animationDuration={1000}
                animationEasing="ease-out"
              />
            )}
          </AreaChart>
        )}
      </div>
    </div>
  )
}
